import React, { useEffect, useMemo, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate } from "react-router-dom";
import { useAuth, hasRole } from "@/contexts/AuthContext";
import { useTableSort } from "@/lib/useTableSort";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, RotateCcw, ArrowRightLeft } from "lucide-react";

const CURRENCIES = ["USD", "CAD", "GBP", "EUR", "INR", "AUD", "AED", "SAR"];

export default function Clients() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [bds, setBds] = useState([]);           // active BD users only for the picker
  const [allBds, setAllBds] = useState([]);      // active + inactive for lookups
  const [allUsers, setAllUsers] = useState([]);  // for BD name display fallback (admin/TS as BD too)
  const [q, setQ] = useState("");
  const [bdFilter, setBdFilter] = useState("all");
  const [countryFilter, setCountryFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyForm = {
    firm_name: "", contact_person_name: "", email: "", phone: "",
    country: "", address: "", default_currency: "USD", tax_number: "", notes: "",
    business_development_owner_id: "",
  };
  const [form, setForm] = useState(emptyForm);

  // --- Reassign BD (with inherited policy) - single-client flow ---
  const [reassigning, setReassigning] = useState(null); // the client being reassigned
  const emptyReassign = {
    to_bd_id: "",
    effective_date: new Date().toISOString().slice(0, 10),
    move_active_projects: true,
    scope: "per_client",
    tiers: [
      { up_to_projects: 5, percentage: 1 },
      { up_to_projects: "", percentage: 0.5 },
    ],
    notes: "",
  };
  const [rsForm, setRsForm] = useState(emptyReassign);
  const openReassign = (c) => { setReassigning(c); setRsForm(emptyReassign); };
  const addRsTier = () => {
    setRsForm((f) => {
      const list = [...(f.tiers || [])];
      const lastIdx = list.length - 1;
      const lastIsOpen = list.length && (list[lastIdx].up_to_projects === "" || list[lastIdx].up_to_projects === null);
      const prevUp = lastIsOpen && lastIdx >= 1 ? Number(list[lastIdx - 1].up_to_projects || 0) : Number(list[lastIdx]?.up_to_projects || 0);
      const newTier = { up_to_projects: prevUp + 5, percentage: 0 };
      if (lastIsOpen) list.splice(lastIdx, 0, newTier); else list.push(newTier);
      return { ...f, tiers: list };
    });
  };
  const removeRsTier = (idx) => setRsForm((f) => {
    const list = [...(f.tiers || [])];
    if (list.length <= 1) return f;
    list.splice(idx, 1);
    return { ...f, tiers: list };
  });
  const setRsTierField = (idx, key, value) => setRsForm((f) => {
    const list = [...(f.tiers || [])];
    list[idx] = { ...list[idx], [key]: value };
    return { ...f, tiers: list };
  });
  const submitReassign = async () => {
    if (!reassigning) return;
    if (!rsForm.to_bd_id) return toast.error("Pick the new BD");
    if (rsForm.to_bd_id === reassigning.business_development_owner_id) return toast.error("Client is already assigned to that BD");
    if (!rsForm.effective_date) return toast.error("Effective date is required");
    // Normalize + validate ladder
    const tiers = (rsForm.tiers || []).map((t) => ({
      up_to_projects: t.up_to_projects === "" || t.up_to_projects === null || t.up_to_projects === undefined
        ? null : Number(t.up_to_projects),
      percentage: Number(t.percentage || 0),
    }));
    if (!tiers.length) return toast.error("At least one tier is required");
    let prev = 0;
    for (let i = 0; i < tiers.length; i++) {
      const t = tiers[i];
      if (t.percentage < 0) return toast.error(`Tier ${i + 1}: percentage must be >= 0`);
      if (t.up_to_projects === null) {
        if (i !== tiers.length - 1) return toast.error("Only the LAST tier may leave 'up to' blank");
      } else {
        if (t.up_to_projects <= prev) return toast.error(`Tier ${i + 1}: 'up to' must be greater than ${prev}`);
        prev = t.up_to_projects;
      }
    }
    try {
      const { data } = await api.post("/api/incentives/reassign-client", {
        client_id: reassigning.id,
        to_bd_id: rsForm.to_bd_id,
        effective_date: rsForm.effective_date,
        move_active_projects: rsForm.move_active_projects,
        inherited_tiers: tiers,
        inherited_scope: rsForm.scope || "per_client",
        inherited_threshold_projects: tiers[0].up_to_projects || 0,
        inherited_primary_percentage: tiers[0].percentage,
        inherited_secondary_percentage: tiers[tiers.length - 1].percentage,
        notes: rsForm.notes || null,
      });
      toast.success(data.message || "Client reassigned");
      setReassigning(null); setRsForm(emptyReassign);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Reassignment failed")); }
  };

  const loadUsers = async () => {
    try {
      const { data } = await api.get("/api/users");
      setAllUsers(data);
      const bdUsers = data.filter((u) => u.role === "business_development");
      setAllBds(bdUsers);
      setBds(bdUsers.filter((u) => u.is_active !== false));
    } catch (e) {
      // BD users hit /api/users through a scoped list — a 403 here is expected.
      if (e.response?.status && e.response.status !== 403) {
        logger.error("loadUsers failed", e);
      }
    }
  };
  const load = async () => {
    const { data } = await api.get(`/api/clients?q=${encodeURIComponent(q)}`);
    setItems(data);
  };
  useEffect(() => { loadUsers(); }, []);
  useEffect(() => { load(); }, [q]);

  const bdMap = useMemo(() => {
    const m = new Map();
    allUsers.forEach((u) => m.set(u.id, u));
    return m;
  }, [allUsers]);

  const countries = useMemo(() => {
    const set = new Set(items.map((c) => (c.country || "").trim()).filter(Boolean));
    return Array.from(set).sort();
  }, [items]);

  const filteredItems = useMemo(() => {
    return items.filter((c) => {
      if (bdFilter !== "all" && c.business_development_owner_id !== bdFilter) return false;
      if (countryFilter !== "all" && (c.country || "") !== countryFilter) return false;
      return true;
    });
  }, [items, bdFilter, countryFilter]);

  const clientColumns = [
    { key: "firm_name", label: "Firm Name" },
    { key: "bd", label: "BD Name", getValue: (c) => (c.business_development_owner_id ? (bdMap.get(c.business_development_owner_id)?.name || "") : "") },
    { key: "contact_person_name", label: "Contact" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "country", label: "Country" },
    { key: "default_currency", label: "Currency" },
    { key: "created_at", label: "Created", getValue: (c) => c.created_at || "" },
  ];
  const { sortedRows: sortedClients, sortBy: clientSort, toggle: toggleClientSort } = useTableSort(filteredItems, clientColumns, "created_at", "desc", { storageKey: "clients" });
  const clientSortIndicator = (key) => (
    clientSort.key === key ? (clientSort.dir === "asc" ? "▲" : "▼") : "↕"
  );

  const resetFilters = () => { setBdFilter("all"); setCountryFilter("all"); setQ(""); };

  const isBd = user?.role === "business_development";
  const isAdmin = user?.role === "admin";
  const canCreate = hasRole(user, "admin", "accounts", "business_development");

  // Strip empty/whitespace-only strings so backend optional fields
  // (email/phone/country/…) don't fail strict validators like EmailStr.
  const cleanPayload = (obj) => {
    const out = {};
    Object.entries(obj || {}).forEach(([k, v]) => {
      if (typeof v === "string") {
        const t = v.trim();
        if (t !== "") out[k] = t;
      } else if (v !== null && v !== undefined) {
        out[k] = v;
      }
    });
    return out;
  };

  const create = async () => {
    if (!form.firm_name?.trim()) { toast.error("Firm Name is required."); return; }
    if (!form.contact_person_name?.trim()) { toast.error("Contact Person is required."); return; }
    try {
      const body = cleanPayload(form);
      // BD role: backend force-assigns self. Never let the client submit a BD id.
      if (isBd) delete body.business_development_owner_id;
      // Always ensure default_currency is present (was cleaned if user cleared it).
      if (!body.default_currency) body.default_currency = "USD";
      // Ensure required fields kept even if only whitespace.
      body.firm_name = form.firm_name.trim();
      body.contact_person_name = form.contact_person_name.trim();
      await api.post("/api/clients", body);
      toast.success(
        isBd ? "Client added — assigned to you"
          : (body.business_development_owner_id ? "Client added" : "Client added — assigned to TS")
      );
      setOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Could not create client — please review the highlighted fields.")); }
  };

  const openEdit = (c) => { setEditing(c); setForm({ ...emptyForm, ...c }); };
  const saveEdit = async () => {
    try {
      const { firm_name, contact_person_name, email, phone,
        country, address, default_currency, tax_number, notes } = form;
      const body = cleanPayload({
        firm_name, contact_person_name, email, phone,
        country, address, default_currency, tax_number, notes,
      });
      if (!body.firm_name) body.firm_name = (firm_name || "").trim();
      if (!body.contact_person_name) body.contact_person_name = (contact_person_name || "").trim();
      // Only admin can change BD owner via update; use dedicated reassign endpoint for that.
      if (isAdmin && form.business_development_owner_id !== editing.business_development_owner_id) {
        await api.post(`/api/clients/${editing.id}/reassign-bd`, {
          business_development_owner_id: form.business_development_owner_id || null,
          reason: "Admin reassignment via client edit",
        });
      }
      await api.patch(`/api/clients/${editing.id}`, body);
      toast.success("Client updated"); setEditing(null); setForm(emptyForm); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Could not update client.")); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete client "${c.firm_name}"? All linked projects and invoices remain but this client will be hidden. Only admins can delete.`)) return;
    try { await api.delete(`/api/clients/${c.id}`); toast.success("Client deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Could not delete client.")); }
  };

  const renderBdOptions = (includeInactive = false) => {
    const list = includeInactive ? allBds : bds;
    return list.map((u) => (
      <SelectItem key={u.id} value={u.id} data-testid={`bd-option-${u.id}`}>
        {u.name}{u.is_active === false ? " (inactive)" : ""}
      </SelectItem>
    ));
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight">Clients</h1>
          <p className="text-sm text-slate-500 mt-1">All firms and their contacts.</p>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-client-btn" className="bg-brand hover:bg-brand-hover">
                <Plus className="w-4 h-4 mr-2" /> Add Client
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>Add Client</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                {[
                  ["firm_name", "Firm Name *"],
                  ["contact_person_name", "Contact Person *"],
                  ["email", "Email"],
                  ["phone", "Phone"],
                  ["country", "Country"],
                  ["tax_number", "Tax / VAT Number"],
                ].map(([k, l]) => (
                  <div key={k} className="space-y-1.5">
                    <Label>{l}</Label>
                    <Input data-testid={`client-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                  </div>
                ))}
                {!isBd && (
                  <div className="space-y-1.5">
                    <Label>BD Name {isAdmin ? "(leave blank = TS)" : ""}</Label>
                    <Select value={form.business_development_owner_id || "__ts__"}
                            onValueChange={(v) => setForm({ ...form, business_development_owner_id: v === "__ts__" ? "" : v })}>
                      <SelectTrigger data-testid="client-bd"><SelectValue placeholder="Assign a BD (blank = TS)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__ts__">TS (unassigned to any BD)</SelectItem>
                        {renderBdOptions(false)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {isBd && (
                  <div className="space-y-1.5">
                    <Label>BD Name</Label>
                    <div className="text-sm text-slate-700 py-2 px-3 bg-slate-50 border border-slate-200 rounded" data-testid="client-bd-auto">
                      {user?.name || "You"} <span className="text-xs text-slate-500">(auto-assigned to you)</span>
                    </div>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label>Default Currency</Label>
                  <Select value={form.default_currency} onValueChange={(v) => setForm({ ...form, default_currency: v })}>
                    <SelectTrigger data-testid="client-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label>Address</Label>
                  <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label>Notes</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button className="bg-brand hover:bg-brand-hover" onClick={create} data-testid="save-client-btn">Save Client</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="border-slate-200 shadow-none p-4 space-y-3">
        {/* Filters — sticky at top when scrolling */}
        <div className="sticky top-0 z-20 bg-white -m-4 mb-3 px-4 py-3 border-b border-slate-100 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px] flex items-center gap-2">
            <Search className="w-4 h-4 text-slate-400" />
            <Input placeholder="Search by firm, contact, email, phone…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="clients-search" />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-wide text-slate-500">BD</Label>
            <Select value={bdFilter} onValueChange={setBdFilter}>
              <SelectTrigger className="w-44" data-testid="client-bd-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All BDs</SelectItem>
                {allBds.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name}{u.is_active === false ? " (inactive)" : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-wide text-slate-500">Country</Label>
            <Select value={countryFilter} onValueChange={setCountryFilter}>
              <SelectTrigger className="w-44" data-testid="client-country-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All countries</SelectItem>
                {countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-wide text-slate-500">Sort</Label>
            <Select
              value={clientSort.key === "created_at" ? (clientSort.dir === "desc" ? "newest" : "oldest") : "custom"}
              onValueChange={(v) => {
                if (v === "newest" && (clientSort.key !== "created_at" || clientSort.dir !== "desc")) toggleClientSort("created_at");
                if (v === "oldest") {
                  if (clientSort.key !== "created_at") toggleClientSort("created_at");
                  if (clientSort.key === "created_at" && clientSort.dir === "desc") toggleClientSort("created_at");
                }
              }}
            >
              <SelectTrigger className="w-44" data-testid="client-sort-order"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest first</SelectItem>
                <SelectItem value="oldest">Oldest first</SelectItem>
                {clientSort.key !== "created_at" && <SelectItem value="custom" disabled>Custom · {clientSort.key}</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" onClick={resetFilters} data-testid="client-reset-filters">
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset filters
          </Button>
          <span className="ml-auto text-xs text-slate-500">
            {filteredItems.length} of {items.length}
          </span>
        </div>

        {/* Table: sticky header + sticky "Firm" & "BD" columns */}
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="max-h-[calc(100vh-260px)] overflow-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="bg-slate-50 sticky top-0 z-30">
                <tr>
                  <th className="px-3 py-2 text-left sticky left-0 bg-slate-50 z-40 border-r border-slate-200 min-w-[220px]">
                    <button type="button" onClick={() => toggleClientSort("firm_name")} className="inline-flex items-center gap-1 hover:text-slate-900" data-testid="sort-firm_name">
                      Firm Name <span className="text-[10px] text-slate-400">{clientSortIndicator("firm_name")}</span>
                    </button>
                  </th>
                  <th className="px-3 py-2 text-left sticky left-[220px] bg-slate-50 z-40 border-r border-slate-200 min-w-[180px]">
                    <button type="button" onClick={() => toggleClientSort("bd")} className="inline-flex items-center gap-1 hover:text-slate-900" data-testid="sort-bd">
                      BD Name <span className="text-[10px] text-slate-400">{clientSortIndicator("bd")}</span>
                    </button>
                  </th>
                  <th className="px-3 py-2 text-left min-w-[160px]">
                    <button type="button" onClick={() => toggleClientSort("contact_person_name")} className="inline-flex items-center gap-1 hover:text-slate-900" data-testid="sort-contact_person_name">
                      Contact <span className="text-[10px] text-slate-400">{clientSortIndicator("contact_person_name")}</span>
                    </button>
                  </th>
                  <th className="px-3 py-2 text-left min-w-[200px]">
                    <button type="button" onClick={() => toggleClientSort("email")} className="inline-flex items-center gap-1 hover:text-slate-900" data-testid="sort-email">
                      Email <span className="text-[10px] text-slate-400">{clientSortIndicator("email")}</span>
                    </button>
                  </th>
                  <th className="px-3 py-2 text-left min-w-[140px]">
                    <button type="button" onClick={() => toggleClientSort("phone")} className="inline-flex items-center gap-1 hover:text-slate-900" data-testid="sort-phone">
                      Phone <span className="text-[10px] text-slate-400">{clientSortIndicator("phone")}</span>
                    </button>
                  </th>
                  <th className="px-3 py-2 text-left min-w-[120px]">
                    <button type="button" onClick={() => toggleClientSort("country")} className="inline-flex items-center gap-1 hover:text-slate-900" data-testid="sort-country">
                      Country <span className="text-[10px] text-slate-400">{clientSortIndicator("country")}</span>
                    </button>
                  </th>
                  <th className="px-3 py-2 text-left min-w-[100px]">
                    <button type="button" onClick={() => toggleClientSort("default_currency")} className="inline-flex items-center gap-1 hover:text-slate-900" data-testid="sort-default_currency">
                      Currency <span className="text-[10px] text-slate-400">{clientSortIndicator("default_currency")}</span>
                    </button>
                  </th>
                  <th className="px-3 py-2 text-left min-w-[110px]">
                    <button type="button" onClick={() => toggleClientSort("created_at")} className="inline-flex items-center gap-1 hover:text-slate-900" data-testid="sort-created_at">
                      Created <span className="text-[10px] text-slate-400">{clientSortIndicator("created_at")}</span>
                    </button>
                  </th>
                  <th className="px-3 py-2 text-left min-w-[120px] sticky right-0 bg-slate-50 z-40 border-l border-slate-200">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedClients.map((c) => (
                  <tr key={c.id} data-testid={`client-row-${c.id}`} className="border-t border-slate-100 hover:bg-slate-50/60">
                    <td className="px-3 py-2 font-medium cursor-pointer hover:text-slate-900 sticky left-0 bg-white hover:bg-slate-50/60 z-10 border-r border-slate-200"
                        onClick={() => navigate(`/clients/${c.id}`)}
                        data-testid={`client-firm-${c.id}`}>
                      {c.firm_name}
                    </td>
                    <td className="px-3 py-2 sticky left-[220px] bg-white hover:bg-slate-50/60 z-10 border-r border-slate-200"
                        data-testid={`client-bd-cell-${c.id}`}>
                      {c.business_development_owner_id
                        ? (bdMap.get(c.business_development_owner_id)?.name || <span className="text-slate-400 italic">unknown</span>)
                        : <span className="text-slate-400 italic">unassigned</span>}
                      {c.bd_autofilled && <span className="ml-1.5 text-[9px] uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1">auto</span>}
                    </td>
                    <td className="px-3 py-2">{c.contact_person_name}</td>
                    <td className="px-3 py-2 text-slate-600">{c.email}</td>
                    <td className="px-3 py-2 text-slate-600">{c.phone}</td>
                    <td className="px-3 py-2">{c.country}</td>
                    <td className="px-3 py-2">{c.default_currency}</td>
                    <td className="px-3 py-2 text-slate-500 text-xs whitespace-nowrap">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                    <td className="px-3 py-2 sticky right-0 bg-white hover:bg-slate-50/60 border-l border-slate-200">
                      <div className="flex gap-1">
                        {hasRole(user, "admin", "business_development") && (
                          <button className="p-1.5 rounded hover:bg-slate-100 text-slate-500" onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`} title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                        )}
                        {hasRole(user, "admin") && (
                          <button className="p-1.5 rounded hover:bg-brand-tint text-brand" onClick={() => openReassign(c)} data-testid={`reassign-client-${c.id}`} title="Reassign BD (with policy)"><ArrowRightLeft className="w-3.5 h-3.5" /></button>
                        )}
                        {hasRole(user, "admin") && (
                          <button className="p-1.5 rounded hover:bg-rose-50 text-rose-500" onClick={() => remove(c)} data-testid={`delete-client-${c.id}`} title="Delete (admin only)"><Trash2 className="w-3.5 h-3.5" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {sortedClients.length === 0 && (
                  <tr><td colSpan={9} className="text-center text-slate-500 py-8">No clients match the current filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) { setEditing(null); setForm(emptyForm); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Edit Client · {editing?.firm_name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            {[
              ["firm_name", "Firm Name *"],
              ["contact_person_name", "Contact Person *"],
              ["email", "Email"],
              ["phone", "Phone"],
              ["country", "Country"],
              ["tax_number", "Tax / VAT / GSTIN"],
            ].map(([k, l]) => (
              <div key={k} className="space-y-1.5">
                <Label>{l}</Label>
                <Input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`edit-client-${k}`} />
              </div>
            ))}
            {isAdmin && (
              <div className="space-y-1.5">
                <Label>BD Name — admin-only reassignment</Label>
                <Select value={form.business_development_owner_id || "__ts__"}
                        onValueChange={(v) => setForm({ ...form, business_development_owner_id: v === "__ts__" ? "" : v })}>
                  <SelectTrigger data-testid="edit-client-bd"><SelectValue placeholder="Assign a BD" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__ts__">TS (unassigned)</SelectItem>
                    {renderBdOptions(true)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {!isAdmin && form.business_development_owner_name_snapshot && (
              <div className="space-y-1.5">
                <Label>BD Name</Label>
                <div className="text-sm text-slate-700 py-2 px-3 bg-slate-50 border border-slate-200 rounded">
                  {form.business_development_owner_name_snapshot || "TS"}
                  <span className="text-xs text-slate-500 ml-2">(only admins can change this)</span>
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Default Currency</Label>
              <Select value={form.default_currency} onValueChange={(v) => setForm({ ...form, default_currency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Address</Label>
              <Textarea value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditing(null); setForm(emptyForm); }}>Cancel</Button>
            <Button className="bg-brand hover:bg-brand-hover" onClick={saveEdit} data-testid="save-edit-client">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Reassign BD (with inherited policy) --- */}
      <Dialog open={!!reassigning} onOpenChange={(v) => { if (!v) { setReassigning(null); setRsForm(emptyReassign); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0" data-testid="reassign-client-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100 shrink-0">
            <DialogTitle>Reassign BD (with policy) · {reassigning?.firm_name}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs px-3 py-2">
              <b>What this does:</b> Moves this client to the new BD, moves their <i>active</i> projects (closed projects freeze under the previous BD), and creates a rule that applies <b>only to this client</b> with the tier ladder below. From the effective date forward, every payment on this client uses this new tier ladder — the new BD's regular rule on other clients stays untouched.
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Current BD</Label>
                <div className="text-sm text-slate-700 py-2 px-3 bg-slate-50 border border-slate-200 rounded" data-testid="reassign-current-bd">
                  {reassigning?.business_development_owner_id
                    ? (bdMap.get(reassigning.business_development_owner_id)?.name || "unknown")
                    : "TS (unassigned)"}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>New BD *</Label>
                <Select value={rsForm.to_bd_id} onValueChange={(v) => setRsForm({ ...rsForm, to_bd_id: v })}>
                  <SelectTrigger data-testid="reassign-new-bd"><SelectValue placeholder="Select the new BD" /></SelectTrigger>
                  <SelectContent>
                    {allBds
                      .filter((u) => u.id !== reassigning?.business_development_owner_id)
                      .map((u) => (
                        <SelectItem key={u.id} value={u.id} data-testid={`reassign-bd-option-${u.id}`}>
                          {u.name}{u.is_active === false ? " (inactive)" : ""}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Effective date *</Label>
                <Input type="date" value={rsForm.effective_date} onChange={(e) => setRsForm({ ...rsForm, effective_date: e.target.value })} data-testid="reassign-effective-date" />
              </div>
              <div className="space-y-1.5">
                <Label>Also move active projects</Label>
                <label className="flex items-center gap-2 text-sm mt-2">
                  <input
                    type="checkbox"
                    checked={rsForm.move_active_projects}
                    onChange={(e) => setRsForm({ ...rsForm, move_active_projects: e.target.checked })}
                    data-testid="reassign-move-projects"
                  />
                  Move active + in-progress projects to the new BD (recommended)
                </label>
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label>Count scope</Label>
                <Select value={rsForm.scope} onValueChange={(v) => setRsForm({ ...rsForm, scope: v })}>
                  <SelectTrigger data-testid="reassign-scope-single"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="per_client">Per client — Nth project of this client</SelectItem>
                    <SelectItem value="per_bd_all_clients">Across all clients — Nth project the new BD closes overall</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Tier ladder for the new BD on this client</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addRsTier} data-testid="reassign-single-add-tier">
                    <Plus className="w-3 h-3 mr-1" />Add tier
                  </Button>
                </div>
                <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
                  {rsForm.tiers.map((t, i) => {
                    const isLast = i === rsForm.tiers.length - 1;
                    const lower = i === 0 ? 1 : (Number(rsForm.tiers[i - 1].up_to_projects) || 0) + 1;
                    const openBand = t.up_to_projects === "" || t.up_to_projects === null || t.up_to_projects === undefined;
                    return (
                      <div key={`rs-tier-${i}`} className="flex items-center gap-2 p-2" data-testid={`reassign-single-tier-row-${i}`}>
                        <span className="text-xs text-slate-500 w-16 shrink-0">Tier {i + 1}</span>
                        <div className="flex items-center gap-1 text-sm">
                          <span className="text-slate-500">projects</span>
                          <span className="font-mono">{lower}</span>
                          <span className="text-slate-500">to</span>
                          <Input type="number" className="w-20 h-8" placeholder={isLast ? "∞" : ""} value={t.up_to_projects ?? ""} onChange={(e) => setRsTierField(i, "up_to_projects", e.target.value)} data-testid={`reassign-single-tier-upto-${i}`} />
                          {isLast && openBand && <span className="text-slate-500 text-xs">(and above)</span>}
                        </div>
                        <div className="flex items-center gap-1 text-sm ml-auto">
                          <span className="text-slate-500">@</span>
                          <Input type="number" step="0.01" className="w-20 h-8" value={t.percentage} onChange={(e) => setRsTierField(i, "percentage", e.target.value)} data-testid={`reassign-single-tier-pct-${i}`} />
                          <span className="text-slate-500">%</span>
                        </div>
                        <button type="button" className="text-slate-400 hover:text-rose-600 disabled:opacity-30" disabled={rsForm.tiers.length <= 1} onClick={() => removeRsTier(i)} data-testid={`reassign-single-tier-remove-${i}`} title="Remove tier"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    );
                  })}
                </div>
                <div className="text-[11px] text-slate-500">
                  Leave the LAST tier's "up to" blank to mean "and above". Ranges are inclusive-lower / inclusive-upper (e.g. 1–5, 6–10, 11+).
                </div>
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label>Notes (optional)</Label>
                <Textarea value={rsForm.notes} onChange={(e) => setRsForm({ ...rsForm, notes: e.target.value })} placeholder="Handover context, reason for reassignment, etc." />
              </div>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-slate-100 shrink-0 bg-white">
            <Button variant="outline" onClick={() => { setReassigning(null); setRsForm(emptyReassign); }}>Cancel</Button>
            <Button className="bg-brand hover:bg-brand-hover" onClick={submitReassign} data-testid="reassign-client-submit">Reassign & Apply Policy</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
